import * as React from 'react';
import styled from 'styled-components';

import Button from 'components/Button';
import Container from 'components/Container';
import Form from 'components/Form';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Row from 'components/Row';
import SleuthTVLogo from 'images/sleuth-tv-logo.svg?svgr';
import Stack from 'components/Stack';
import VisuallyHidden from 'components/VisuallyHidden';

import { PAGE_REGIONS, getPageRegionProps } from 'utils';
import { color, font, media } from 'styles/utils';
import useForm from 'hooks/useForm';
import useTrackEvent from 'hooks/useTrackEvent';

import badgeG2 from 'images/badge-g2-winter-2025.svg';
import badgeAtlassianMarketplace from 'images/badge-atlassian-marketplace.svg';
import badgeGithubMarketplace from 'images/badge-github-marketplace.svg';

import EngineeringOnEfficiency from './EngineeringOnEfficiency';
import { StaticImage } from 'gatsby-plugin-image';

const StyledFooter = styled.footer`
  background-color: ${color('darkestTeal')};
  color: ${color('white')};
  font-size: 0.875rem;
  flex: 1;
`;

const StyledContainer = styled(Container)`
  display: grid;
  gap: 3.5rem;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: max-content;
  padding: 6rem 1rem;

  ${media.min('md')} {
    padding: 4rem 1rem;
  }

  &:not(:first-child) {
    padding-top: 0;

    ${media.min('md')} {
      padding-top: 0;
    }
  }

  & > * {
    max-width: 100%;
  }

  ${media.min('lg')} {
    grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
    justify-content: space-between;
    justify-items: start;
  }
`;

const StyledLogo = styled(SleuthTVLogo)`
  margin-bottom: 1.5rem;
`;

const StyledSubmitter = styled(Form.Submitter)`
  background: ${color('darkTeal')};
`;

const StyledLink = styled(Link)`
  transition: opacity 150ms ease-in-out;
  ${media.hover} {
    &:hover {
      opacity: 0.75;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NavList = styled(({ depth = 0, items, ...props }) => (
  <ul data-depth={depth} role="list" {...props}>
    {items.map((item) => (
      <li key={`${item.label}${item.href}`}>
        <NavListItem data-depth={depth} variant="navItem" {...item} />
        {item.items && <NavList depth={depth + 1} items={item.items} />}
      </li>
    ))}
  </ul>
))`
  display: flex;
  flex-direction: column;

  &[data-depth='0'] {
    column-gap: min(10%, 3.5rem);
    display: grid;
    flex-direction: row;
    grid-template-columns: repeat(2, minmax(0, max-content));
    row-gap: 3rem;

    ${media.min('48rem')} {
      display: flex;
    }
  }
`;

const NavListItem = styled(({ href, label, onClick, variant, ...props }) => (
  <Button
    as={href ? undefined : 'span'}
    href={href}
    size="sm"
    variant={variant === 'navItem' ? null : variant}
    {...props}
  >
    {label}
  </Button>
))`
  color: ${color('lightestBlue')};
  display: block;
  font-weight: normal;
  padding: 0.75rem 0;
  text-decoration: none;
  transition: opacity 150ms ease-in-out;

  ${media.hover} {
    &:hover {
      opacity: 0.75;
    }
  }

  &[data-depth='0'] {
    color: ${color('lightestTeal')};
    font-family: ${font('heading')};
    font-weight: bold;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }
`;

const socialLinks = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/SleuthHQ/',
  },
  {
    label: 'Twitter',
    href: 'https://twitter.com/sleuth_io',
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/c/sleuthtv',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/sleuth-io/',
  },
];

const badges = [
  { src: badgeG2, width: 82, height: 107 },
  { src: badgeAtlassianMarketplace, width: 90, height: 107 },
  { src: badgeGithubMarketplace, width: 90, height: 107 },
  {
    component: (
      <StaticImage
        alt=""
        layout="fixed"
        src="../../images/badge-SOC2.png"
        width={95}
        key="badge-SOC2"
      />
    ),
  },
];

export default function Footer({ menu, withCta }) {
  const { trackEvent } = useTrackEvent();
  const year = React.useRef(new Date().getFullYear());
  const form = useForm({
    onSubmit: (event, { formValues, submitterValue }) =>
      trackEvent('content_subscribed', {
        object: 'content',
        action: 'subscribed',
        label: submitterValue,
        location: PAGE_REGIONS.footer,
        pageUrl: window.location.href,
        content_name: 'Sleuth TV',
        email: formValues.email,
      }),
  });

  return (
    <StyledFooter {...getPageRegionProps(PAGE_REGIONS.footer)}>
      {withCta && <EngineeringOnEfficiency />}

      <StyledContainer>
        {/* Footer navigation */}
        <Nav aria-label={menu.label}>
          <NavList items={menu.items || []} />
        </Nav>

        {/* Newsletter signup */}
        <Stack gap="1rem" align="start">
          <StyledLogo aria-hidden="true" />
          <Form {...form.getFormProps()}>
            {form.isSubmitted ? (
              <Form.Message>
                {form.isSubmitFailure &&
                  'Something went wrong. Please try again later.'}
                {form.isSubmitSuccess &&
                  "Thank you! You'll be notified of new shows and episodes ✅"}
              </Form.Message>
            ) : (
              <>
                <label htmlFor="footer-email">
                  Get notified of the latest from Sleuth TV
                </label>
                <Form.ControlGroup>
                  <Form.Control
                    autocomplete="email"
                    id="footer-email"
                    name="email"
                    placeholder="Email"
                    required
                    type="email"
                  />
                  <StyledSubmitter variant={null}>
                    Sign Up &ensp;
                    <Icon name="arrow-up-right" size="sm" />
                  </StyledSubmitter>
                </Form.ControlGroup>
              </>
            )}
          </Form>
        </Stack>

        {/* Badges */}
        <Row gap="2rem" justify="start" layout="auto">
          {badges.map(
            ({ component, src, width, height }) =>
              component || (
                <img
                  alt=""
                  decoding="async"
                  height={height}
                  key={src}
                  loading="lazy"
                  src={src}
                  width={width}
                />
              )
          )}
        </Row>

        {/* Social links and copyright */}
        <Stack gap="1em">
          <Row
            breakpoint={null}
            css="font-size: 1.5rem;"
            gap="1.5rem"
            justify="start"
            layout="auto"
          >
            {socialLinks.map(({ label, href }) => (
              <StyledLink key={label} href={href}>
                <VisuallyHidden>{label}</VisuallyHidden>
                <Icon name={label.toLowerCase()} />
              </StyledLink>
            ))}
          </Row>
          <span>© Sleuth {year.current}</span>
        </Stack>
      </StyledContainer>
    </StyledFooter>
  );
}
